import { keyBy } from 'lodash';

import { type CustomFieldFilterType, type FilterType } from '~/components/FiltersMenu/types';
import { FilterParamNames } from '~/constants/search';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { uuidRegExpString } from '~/utils/Url';

const cfIdRegExp = new RegExp(`${uuidRegExpString}$`, 'i');

/**
 * This method is used to get number of applied filters from defined set of filters and custom fields
 * It is used e.g. in All content page, where dropdown does not keep all filters - some of them are in filter bar
 * e.g:
 * - dropdown contains filters: source, uploader and customFieldA
 * - filter bar contains filters: tag, customFieldB
 * So we want to count how many filters from set (source, uploader, customFieldA) are applied
 */
export const useFiltersCountAppliedFromDropdown = ({
  filtersToCount,
  customFieldsToCount,
}: {
  filtersToCount: FilterType[];
  customFieldsToCount: CustomFieldFilterType[];
}) => {
  const {
    filters,
    filters: {
      modifiedDateEnd,
      modifiedDateStart,
      recordedDateStart,
      recordedDateEnd,
      uploadedDateStart,
      uploadedDateEnd,
      customFields = {},
      type,
      source,
      color,
      colorThreshold,
      bookmarked,
      hasMultipleVersions,
      hasOpenComments,
      untagged,
      isOnBoards,
      ext,
      tagIdAnd,
      tagIdNot,
      tagIdOr,
      uploaderId,
    },
  } = useFiltersContext();

  let filtersCount = 0;

  const customFieldsById = keyBy(customFieldsToCount, 'id');

  if (filtersToCount.includes('dateCreated')) {
    if (recordedDateStart || recordedDateEnd) filtersCount += 1;
  }
  if (filtersToCount.includes('dateModified')) {
    if (modifiedDateEnd || modifiedDateStart) filtersCount += 1;
  }
  if (filtersToCount.includes('dateUploaded')) {
    if (uploadedDateEnd || uploadedDateStart) filtersCount += 1;
  }
  if (filtersToCount.includes('dateTaken')) {
    const takenDateStart = filters[FilterParamNames.takenDateStart];
    const takenDateEnd = filters[FilterParamNames.takenDateEnd];
    if (takenDateStart || takenDateEnd) filtersCount += 1;
  }

  if (filtersToCount.includes('customField') && customFieldsToCount.length > 0) {
    const cfIds = Object.keys(customFields).map((param) => param.match(cfIdRegExp));
    cfIds.forEach((matchedId) => {
      if (matchedId && matchedId[0] && customFieldsById[matchedId[0]]) {
        filtersCount += 1;
      }
    });
  }

  if (
    filtersToCount.includes('camera') &&
    (filters[FilterParamNames.cameraMake] || filters[FilterParamNames.cameraModel])
  )
    filtersCount += 1;

  if (filtersToCount.includes('type') && !!type) filtersCount += 1;
  if (filtersToCount.includes('color') && (!!color || !!colorThreshold)) filtersCount += 1;
  if (filtersToCount.includes('extension') && !!ext) filtersCount += 1;
  if (filtersToCount.includes('uploader') && !!uploaderId) filtersCount += 1;
  if (filtersToCount.includes('source') && !!source) filtersCount += 1;
  if (filtersToCount.includes('tags')) {
    if (tagIdAnd) filtersCount += 1;
    if (tagIdOr) filtersCount += 1;
    if (tagIdNot) filtersCount += 1;
  }
  if (filtersToCount.includes('other')) {
    if (bookmarked) filtersCount += 1;
    if (hasMultipleVersions) filtersCount += 1;
    if (untagged) filtersCount += 1;
    if (hasOpenComments) filtersCount += 1;
    if (isOnBoards) filtersCount += 1;
  }

  if (filtersToCount.includes('country') && filters[FilterParamNames.country]) filtersCount += 1;
  if (filtersToCount.includes('state') && filters[FilterParamNames.state]) filtersCount += 1;
  if (filtersToCount.includes('city') && filters[FilterParamNames.city]) filtersCount += 1;

  if (filtersToCount.includes('importedKeywords')) {
    if (filters[FilterParamNames.importedKeywordNot]) filtersCount += 1;
    if (filters[FilterParamNames.importedKeywordAnd]) filtersCount += 1;
    if (filters[FilterParamNames.importedKeywordOr]) filtersCount += 1;
  }

  if (filtersToCount.includes('copyright') && filters[FilterParamNames.copyright]) filtersCount += 1;

  if (filtersToCount.includes('creator') && filters[FilterParamNames.creator]) filtersCount += 1;

  if (filtersToCount.includes('videoFrameRate') && filters[FilterParamNames.videoFrameRate]) filtersCount += 1;
  if (filtersToCount.includes('videoAspectRatio') && filters[FilterParamNames.videoAspectRatio]) filtersCount += 1;
  if (filtersToCount.includes('audioCoding') && filters[FilterParamNames.audioCoding]) filtersCount += 1;
  if (filtersToCount.includes('audioSampleRate') && filters[FilterParamNames.audioSampleRate]) filtersCount += 1;
  if (filtersToCount.includes('people')) {
    if (filters[FilterParamNames.personIdAnd]) filtersCount += 1;
    if (filters[FilterParamNames.personIdOr]) filtersCount += 1;
  }

  if (filtersToCount.includes('orientation') && filters[FilterParamNames.orientation]) filtersCount += 1;

  return { filtersCount };
};
